import React, { useEffect, useState, useCallback } from 'react';
import _ from 'lodash';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { useTranslator } from '@jutro/locale';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { MLVeracityService, MLMultiLobsDataHolderService } from 'ml-services-common-react';
import { MLModalService } from 'ml-portals-services-react';
import { platformMessages } from 'ml-components-platform-react';
import { MLDisclosureUtil } from 'ml-portals-util-js';
import appConfig from 'app-config';
import metadata from './VeracityCheckPage.metadata.json5'; 
import messages from './VeracityCheckPage.messages';
import styles from './VeracityCheckPage.module.scss';
import {UserService} from "gw-capability-gateway";

const VeracityCheckPage = (props) => {
    const {
        wizardData: submissionVM, currentStep, goNext, isSkipping
    } = props;

    const isGpa = appConfig.portalCode === 'GPA';
    const isQNB = appConfig.portalCode === 'QB';
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const translator = useTranslator();
    const { authHeader } = useAuthentication();

    const [showVeracityFrame, setShowVeracityFrame] = useState(false);
    const [veracityURL, setVeracityURL] = useState('');
    const [showHeader, setShowHeader] = useState(false);


    const {
        showProgressDialog,
        showErrorModal
    } = MLModalService();

    useEffect(() => {
        sessionStorage.setItem('veracity-check', appConfig.portalCode === 'QB')
    },[])

    const onNext = useCallback(async(skipping = false) => {
        if (isGpa && currentStep.mlStepName === 'paVeracityCheck') {
            MLDisclosureUtil.setRequoteAfterMvr(submissionVM.quoteID.value, false);
        }

        if (currentStep.mlStepName === 'compPaVeracityCheckPostLanding') {
            MLMultiLobsDataHolderService.storeFlowSteps(currentStep.mlStepName, 'compPaGroupAssociations', authHeader);
        }

        if (skipping) {
            goNext();
            
            return;
        }

        if (_.isUndefined(_.get(submissionVM, 'baseData.bypassCreditOrdering_Ext.value'))){
            const user = await UserService.getCurrentUser(authHeader);
            if ( user?.primaryProducerCode_ML?.code?.startsWith('9SM')) {
                _.set(submissionVM, 'baseData.bypassCreditOrdering_Ext.value', true);
            } else {
                _.set(submissionVM, 'baseData.bypassCreditOrdering_Ext.value', false);
            }
        }

        _.set(submissionVM, 'portalCurrentStep_ML.value', 'paVeracityCheck');
        _.set(submissionVM, 'bypassValidationForComprater_Ext.value', true);
        
        const promise = (currentStep.mlStepName === 'paVeracityCheck')
            ? LoadSaveService.updateQuotedSubmission(submissionVM.value, authHeader)
            : LoadSaveService.updateDraftSubmission(submissionVM.value, authHeader);
        
        showProgressDialog(platformMessages.saveSubmission, promise);
        promise.then((res) => {
            submissionVM.value = res;
            goNext();
        });
    }, [LoadSaveService, authHeader, currentStep.mlStepName, goNext, isGpa, showProgressDialog, submissionVM]);

    const handleVeracityError = (error) => {
        console.error(error);        
        
        const contactUsError = {
            customError: {
                quoteID: _.get(submissionVM, 'quoteID.value')
            }
        };
        // Running Promise.reject will throw us out of wizard and to contact us, passing in error state
        // eslint-disable-next-line padding-line-between-statements
        Promise.reject(((isQNB)? error : contactUsError));
    };

    const getVeracityDetails = (callback) => {
        const request = {
            jobNumber: submissionVM.quoteID.value,
            sessionUUID: submissionVM.sessionUUID.value
        };

        const promise = MLVeracityService.getVeracityDetailsByJobNumber(request, authHeader);
        
        showProgressDialog(platformMessages.loading, promise);
        promise.then(callback).catch((error) => {
            handleVeracityError(error);
        });
    };

    const saveSubmission = () => {
        _.set(submissionVM, 'portalCurrentStep_ML.value', 'paVeracityCheck');
        _.set(submissionVM, 'bypassValidationForComprater_Ext.value', true);


        const savePromise = (currentStep.mlStepName === 'paVeracityCheck')
            ? LoadSaveService.updateQuotedSubmission(submissionVM.value, authHeader)
            : LoadSaveService.updateDraftSubmission(submissionVM.value, authHeader);
        
        showProgressDialog(platformMessages.loading, savePromise);
    };

    const exit = () => {
        if (isGpa) {
            MLDisclosureUtil.setRequoteAfterMvr(submissionVM.quoteID.value, false);
        }

        const landingPagePath = isGpa ? 'producer-engage' : 'quote-and-buy';
        const url = `${window.location.origin}/${landingPagePath}`;
        
        window.location.href = url;
    };

    const onSaveComplete = (data) => {
        if (data.status === 'save_return') {
            exit();
        } else if (data.status === 'complete' && data.recommendedAction === 'Decline') {
            // if recommendedAction is Decline, save the submission to update the alert and do not move from the screen
            saveSubmission();
        } else {
            onNext();
        }
    };

    const onError = () => {
        const title = translator(messages.errorTitle);
        const description = translator(messages.errorDescription);
        
        showErrorModal(title, description);
    };

    const handleVeracityEvent = (ev) => {
        if (ev.data.fromEvent === 'veracityIntegration') {
            const { data } = ev;
            
            if (data.action === 'onSaveComplete') {
                onSaveComplete(data.veracityData);
            } else if (data.action === 'onError') {
                onError(data.veracityData);
            } else {
                console.error(new Error('Unidentified additional details interaction'));
            }
        }
    };

    const registerMessageEventListener = () => {
        window.addEventListener('message', handleVeracityEvent);
    };

    const destroyMessageEventListener = () => {
        window.removeEventListener('message', handleVeracityEvent);
    };

    const setVeracityDetails = (details) => {
        setVeracityURL(`/html/ml-veracity-iframe.html?tokenstring=${details.idResolveToken}&veracityIntegration=${details.idResolveUrl}`);
        setShowVeracityFrame(details.displayIdResolve);
    };

    const showLoadingWhileIframeIsLoaded = () => {
        const promise = new Promise((resolve) => {
            const maxWait = 5;
            let counter = 0;

            const intervalID = setInterval(() => {
                if (counter > maxWait) {
                    setShowHeader(true);
                    resolve();
                    window.clearInterval(intervalID);
                }

                const iframe = document.getElementById('veracityFrame')
                const idResolvePanel = iframe.contentWindow.document.getElementsByClassName('idf-pages-wrapper')
              
                if (idResolvePanel.length > 0) {
                    setShowHeader(true);
                    resolve();
                    window.clearInterval(intervalID);
                }
                counter += 1;
            }, 2000);
        });
        
        showProgressDialog(platformMessages.loading, promise);
    };

    const skipOnResumeIf = () => {
        if (currentStep.mlStepName !== 'paVeracityCheck' && currentStep.mlStepName !== 'compPaVeracityCheckPostLanding') {
            return true;
        }

        if (currentStep.mlStepName === 'compPaVeracityCheckPostLanding') {
            return MLMultiLobsDataHolderService.hasCompletedStep(currentStep.mlStepName);
        }

        // Skip to Confirmation Page for policy with a policy number
        if (!!_.get(submissionVM, 'bindData.policyNumber.value') && (submissionVM.value.baseData.status_ML === 'Bound'
            || submissionVM.value.baseData.status_ML === 'Binding')) {
            return true;
        }

        if ( _.get(submissionVM, 'isBundle_ML.value', false) && _.get(submissionVM, 'baseData.status_ML.value') !== 'Draft' &&
                MLMultiLobsDataHolderService.isBeforeLastPurchaseFlowStepVisited('paVeracityCheck', submissionVM.value)) {
            return true;
        };

        return false;
    };

    useEffect(() => {
        if (!isSkipping || (isSkipping && !skipOnResumeIf())) {
            getVeracityDetails((data) => {
                if (data.displayIdResolve) {
                    registerMessageEventListener();
                    setVeracityDetails(data);
                    showLoadingWhileIframeIsLoaded();
                } else {
                    onNext(true);
                }
            });
            
            return () => {
                destroyMessageEventListener();
            };
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const overrides = {
        gpaPageHeaderContainer: {
            visible: isGpa && showHeader
        },
        veracityFrame: {
            visible: showVeracityFrame,
            src: veracityURL
        }
    };

    const resolvers = {
        resolveClassNameMap: styles
    };


    return (
        <WizardPage
            skipWhen={skipOnResumeIf}
            showCancel={false}
            showPrevious={false}
            showNext={false}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                overrideProps={overrides}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
};

VeracityCheckPage.propTypes = wizardProps;
export default VeracityCheckPage;
